import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ANGULAR_MODULES, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { VendorService } from 'src/app/services/Vendor.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { PhoneFormatPipe } from 'src/app/Shared/phone-format.pipe';

@Component({
  selector: 'app-ops-vendor-attachments',
  standalone: true,
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, FormsModule, PhoneFormatPipe, WordReplacePipe],
  templateUrl: './ops-vendor-attachments.html',
  styleUrl: './ops-vendor-attachments.scss'
})

export class OpsVendorAttachments implements OnInit, OnDestroy {
  @ViewChild('previewIframe') previewIframe!: ElementRef<HTMLIFrameElement>;
  @Input() vendorId!: number;
  private destroy$ = new Subject<void>();

  vendorInfo: any;
  vendorMarketID = '';
  vendorName = '';
  telephone = '';
  Email = '';
  attachmentForm!: FormGroup;
  attachments: any[];
  isImageFile = false;
  isPdfFile = false;
  previewUrl: SafeResourceUrl | null = null;
  previewFileUrl: SafeResourceUrl | null = null;
  originalExtension: string = '';
  loading = false;

  constructor(
    private vendorService: VendorService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.getVendorInfo();
    this.getVendorAttachments();
  }

  initForm(): void {
    this.attachmentForm = this.fb.group({
      VendorId: [this.vendorId],
      AttachmentName: ['', [Validators.required, Validators.maxLength(100)]],
      VendorAttachmentId: [null],
      UploadedFileContent: [null, Validators.required],
      FileType: [null],
      Notes: ['', [Validators.required, Validators.maxLength(150)]],
      DeleteFlag: [false],
      AddedDate: [new Date()]
    });
  }

  getVendorInfo() {
    this.vendorService.GetVendorById(this.vendorId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.vendorInfo = res;
        this.vendorMarketID = res.MarketVendorId;
        this.vendorName = res.VendorName;
        this.telephone = res.Telephone;
        this.Email = res.Email;
      },
      error: (err) => console.error('Failed to load vendor info', err)
    });
  }

  getVendorAttachments() {
    this.vendorService.getVendorAttachmentsById(this.vendorId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res && Array.isArray(res)) {
          this.attachments = res
            .filter((a: any) => !a.DeleteFlag)
            .map((a: any) => {
              const fileTypeLabel =
                a.FileType === 57 ? 'Image' :
                  a.FileType === 58 ? 'PDF' : 'Other';
              return {
                attachmentId: a.VendorAttachmentId,
                Attachments: a.AttachmentName,
                AddedOn: a.AddedDate ? new Date(a.AddedDate + "Z") : null,
                AddedBy: a.UpdatedUserName,
                fileName: a.FileName,
                fileType: a.FileType,
                fileTypeLabel: fileTypeLabel,
                Notes: a.Notes,
              };
            });
        } else {
          this.attachments = [];
        }
      },
      error: (err) => {
        console.error('Failed to load vendor attachments', err);
        this.toastr.error($localize`Failed to load vendor attachments`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          title: 'Invalid File',
          text: 'Only JPG, JPEG, PNG and PDF files are allowed.',
          icon: 'warning'
        });
        input.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64Result = reader.result as string;
        const base64Content = base64Result.split(',')[1];

        let fileTypeCode = null;
        if (file.type === 'application/pdf') {
          fileTypeCode = 58;
        } else if (file.type === 'image/jpeg' || file.type === 'image/png') {
          fileTypeCode = 57;
        }

        this.originalExtension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
        const baseName = file.name.replace(/\.[^/.]+$/, "");

        this.attachmentForm.patchValue({
          VendorId: this.vendorId,
          UploadedFileContent: base64Content,
          FileType: fileTypeCode,
          DeleteFlag: false
        });

        this.isPdfFile = file.type === 'application/pdf';
        this.isImageFile = !this.isPdfFile;
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base64Result);
      };
      reader.readAsDataURL(file);
    }
  }

  getFinalAttachment(): any {
    const formValue = this.attachmentForm.value;
    const trimmedName = (formValue.AttachmentName ?? '').trim();
    const trimmedNotes = (formValue.Notes ?? '').trim();
    const safeName = trimmedName.replace(/\.[^/.]+$/, '');
    const finalName = `${safeName}${this.originalExtension}`;

    return {
      ...formValue,
      AttachmentName: finalName,
      Notes: trimmedNotes
    };
  }

  validateAttachmentForm(): boolean {
    if (this.attachmentForm.invalid) {
      this.attachmentForm.markAllAsTouched();
      const errors: string[] = [];
      const name = this.attachmentForm.get('AttachmentName');
      if (name?.errors?.['required']) {
        errors.push('Attachment Name');
      }
      const notes = this.attachmentForm.get('Notes');
      if (notes?.errors?.['required']) {
        errors.push('Notes');
      }
      const file = this.attachmentForm.get('UploadedFileContent');
      if (file?.errors?.['required']) {
        errors.push('File Upload');
      }
      if (errors.length > 2) {
        this.toastr.warning($localize`Please upload a file and fill in all required fields`, $localize`Validation`,
          this.sharedHTMLSettings.ToasterConfig
        );
      } else if (errors.length > 0) {
        this.toastr.warning($localize`Please fill the required fields: ${errors.join(', ')}`, $localize`Validation`,
          this.sharedHTMLSettings.ToasterConfig
        );
      }
      return false;
    }
    return true;
  }

  saveAttachment(): void {
    if (!this.validateAttachmentForm()) {
      return;
    }

    Swal.fire({
      title: `Are you sure you want to add this attachment?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, add it`,
      cancelButtonText: 'Cancel',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.closeModalById('attachmentModal')
        const payload = this.getFinalAttachment();
        this.vendorService.saveVendorAttachment(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              if (res.FileUrl && !payload.DeleteFlag) {
                this.previewUrl = res.FileUrl;
                this.toastr.success($localize`Attachment uploaded successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
              } else if (payload.DeleteFlag) {
                this.previewUrl = null;
                this.toastr.success(
                  $localize`Attachment deleted successfully`,
                  $localize`Success`,
                  this.sharedHTMLSettings.ToasterConfig
                );
              }

              this.getVendorAttachments();
              setTimeout(() => this.resetAttachmentForm(), 500);
            },
            error: (err) => {
              console.error('Failed to save attachment', err);
              this.toastr.error($localize`Failed to save attachment`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
            }
          });
      }
    });
  }

  deleteAttachment(attachment: any): void {
    Swal.fire({
      title: `Are you sure you want to delete "${attachment.attachments}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.attachmentForm.patchValue({
          VendorAttachmentId: attachment.attachmentId,
          VendorId: this.vendorId,
          DeleteFlag: true
        });

        const payload = this.attachmentForm.value;
        this.vendorService.saveVendorAttachment(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toastr.success($localize`Attachment deleted successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
              this.getVendorAttachments();
            },
            error: (err) => {
              console.error('Failed to delete attachment', err);
              this.toastr.error($localize`Failed to delete attachment`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
            }
          });
      }
    });
  }

  openPreview(data: any) {
    if (!data?.fileName) return;

    this.isImageFile = false;
    this.isPdfFile = false;
    this.previewFileUrl = null;
    this.loading = true;
    this.openModalById('previewAttachmentModal')
    this.vendorService.getVendorAttachmentImage(data.fileName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          const blobUrl = URL.createObjectURL(blob);

          if (data.fileType == 57) { // Image
            this.isImageFile = true;
            this.previewFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
          } else if (data.fileType == 58) { // PDF
            this.isPdfFile = true;
            setTimeout(() => {
              if (this.previewIframe?.nativeElement) {
                this.previewIframe.nativeElement.src = blobUrl;
              }
            });
          }
          this.loading = false;
        },
        error: (err) => {
          console.error("Error fetching file:", err);
          this.loading = false;
        }
      });
  }

  resetAttachmentForm(): void {
    this.initForm();
    this.previewUrl = null;
    this.isImageFile = false;
    this.isPdfFile = false;
    const fileInput = document.querySelector<HTMLInputElement>('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  }

  addAttachment(): void {
    this.resetAttachmentForm();
    this.openModalById('attachmentModal');
  }

  closeModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const modalInstance = window['bootstrap'].Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
    document.body.classList.remove('modal-open');
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  openModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      let modal = window['bootstrap'].Modal.getInstance(modalEl);
      if (!modal) {
        modal = new window['bootstrap'].Modal(modalEl, { backdrop: 'static', keyboard: false });
      }
      modal.show();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}